import { ButtonHTMLAttributes, type ReactNode } from 'react';
import { motion, type HTMLMotionProps } from 'framer-motion';
import { cn } from '../../utils/cn';

interface GlassButtonProps extends Omit<HTMLMotionProps<"button">, "ref"> {
    children: ReactNode;
    variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
    size?: 'sm' | 'md' | 'lg';
    icon?: ReactNode;
}

export default function GlassButton({
    children,
    className,
    variant = 'primary',
    size = 'md',
    icon,
    ...props
}: GlassButtonProps) {
    const variants = {
        primary: "bg-white text-black hover:bg-gray-100 shadow-[0_0_20px_rgba(255,255,255,0.3)] hover:shadow-[0_0_25px_rgba(255,255,255,0.45)] border border-white",
        secondary: "bg-white/[0.1] backdrop-blur-md border border-white/[0.1] text-white hover:bg-white/[0.15] hover:border-white/[0.2]",
        outline: "bg-transparent border border-white/[0.2] text-white hover:border-white/[0.5] hover:bg-white/[0.05]",
        ghost: "bg-transparent text-gray-400 hover:text-white hover:bg-white/[0.05]"
    };

    const sizes = {
        sm: "px-4 py-2 text-sm",
        md: "px-6 py-3 text-base",
        lg: "px-8 py-4 text-lg font-semibold"
    };

    return (
        <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className={cn(
                "relative rounded-full transition-all duration-300 flex items-center justify-center gap-2",
                variants[variant],
                sizes[size],
                className
            )}
            {...props}
        >
            {children}
            {icon && <span className="text-xl">{icon}</span>}
        </motion.button>
    );
}
